<!-- Hover3DEffect.svelte - 3D tilt effect with smooth animations -->
<script>
	/**
	 * @typedef {Object} Props
	 * @property {number} [intensity=1] - Controls rotation intensity (0-3 recommended)
	 * @property {boolean} [shadow=true] - Enable shadow effect
	 * @property {boolean} [glare=false] - Enable light glare effect
	 * @property {number} [damping=0.95] - Smooth animation factor (0-1)
	 * @property {boolean} [depth=false] - Give children elements depth
	 */
	const props = $props();

	// Extract props with defaults
	const intensity = props.intensity ?? 0.3;
	const shadow = props.shadow ?? false;
	const glare = props.glare ?? false;
	const damping = props.damping ?? 0.95;
	const depth = props.depth ?? false;

	// Combined state object
	const state = $state({
		rx: 0,
		ry: 0,
		tx: 0,
		ty: 0,
		hover: false,
		size: { w: 0, h: 0 },
		initialized: false,
		mousePos: { x: 0, y: 0 }
	});

	/** @type {HTMLElement} */
	let el;
	/** @type {number|null} */
	let frame = null;

	// Calculate perspective based on element size
	const perspective = $derived(Math.max(state.size.w, state.size.h) * 2.5);

	// Shadow and glare effects
	const s = $derived(
		shadow
			? {
					x: -state.ry * 0.8,
					y: state.rx * 0.8,
					blur: Math.sqrt(Math.abs(state.rx * state.ry)) * 3,
					opacity: Math.min(Math.sqrt(state.rx * state.rx + state.ry * state.ry) / 25, 0.3)
				}
			: { x: 0, y: 0, blur: 0, opacity: 0 }
	);

	const g = $derived(
		glare
			? {
					opacity: Math.min(Math.sqrt(state.rx * state.rx + state.ry * state.ry) / 30, 0.15),
					x: 50 + state.ry * 1.5,
					y: 50 - state.rx * 1.5
				}
			: { opacity: 0, x: 50, y: 50 }
	);

	// Initialize element size measurement early to prevent jumps
	$effect(() => {
		if (el && !state.initialized) {
			const { width, height } = el.getBoundingClientRect();
			if (width > 0 && height > 0) {
				state.size = { w: width, h: height };
				state.initialized = true;
			}
		}
	});

	// Animation loop with damping
	function animate() {
		if (Math.abs(state.tx - state.rx) < 0.01 && Math.abs(state.ty - state.ry) < 0.01) {
			frame = null;
			return;
		}

		state.rx += (state.tx - state.rx) * (1 - damping);
		state.ry += (state.ty - state.ry) * (1 - damping);
		frame = requestAnimationFrame(animate);
	}

	/**
	 * Calculate rotation values based on mouse position
	 * @param {number} x - Mouse X coordinate
	 * @param {number} y - Mouse Y coordinate
	 * @param {DOMRect} rect - Element bounding rectangle
	 */
	function calculateRotation(x, y, rect) {
		// Calculate mouse position as percentage from center (-1 to 1)
		const px = (x - (rect.left + rect.width / 2)) / (rect.width / 2);
		const py = (y - (rect.top + rect.height / 2)) / (rect.height / 2);

		// Clamp values to prevent extreme angles
		const clampedPx = Math.max(-1, Math.min(1, px));
		const clampedPy = Math.max(-1, Math.min(1, py));

		// Set target with smoothed intensity factor
		state.tx = -clampedPy * 15 * intensity;
		state.ty = clampedPx * 15 * intensity;
	}

	/**
	 * @param {MouseEvent} e
	 */
	function onMove(e) {
		if (!el) return;

		const rect = el.getBoundingClientRect();

		// Only update size if changed significantly to avoid minor recalculations
		if (Math.abs(state.size.w - rect.width) > 1 || Math.abs(state.size.h - rect.height) > 1) {
			state.size = { w: rect.width, h: rect.height };
		}

		state.mousePos = { x: e.clientX, y: e.clientY };
		calculateRotation(e.clientX, e.clientY, rect);

		// Special handling for first hover
		if (!state.hover) {
			state.hover = true;

			// Start closer to target position for smoother entry
			if (state.rx === 0 && state.ry === 0) {
				state.rx = state.tx * 0.5;
				state.ry = state.ty * 0.5;
			}
		}

		if (!frame) frame = requestAnimationFrame(animate);
	}

	function onLeave() {
		state.tx = 0;
		state.ty = 0;
		state.hover = false;

		if (!frame) frame = requestAnimationFrame(animate);
	}

	/**
	 * @param {MouseEvent} e
	 */
	function onEnter(e) {
		if (!el) return;

		const rect = el.getBoundingClientRect();

		// Update size and set mouse position immediately
		state.size = { w: rect.width, h: rect.height };
		state.mousePos = { x: e.clientX, y: e.clientY };
		state.hover = true;

		// Pre-calculate rotation to avoid jump on first move
		calculateRotation(e.clientX, e.clientY, rect);

		// Set initial rotation to a fraction of target for smooth entry
		state.rx = state.tx * 0.2;
		state.ry = state.ty * 0.2;

		if (!frame) frame = requestAnimationFrame(animate);
	}

	// Cleanup on component destruction
	function cleanup() {
		if (frame) {
			cancelAnimationFrame(frame);
			frame = null;
		}
	}

	$effect(() => {
		return cleanup;
	});
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	bind:this={el}
	onmousemove={onMove}
	onmouseleave={onLeave}
	onmouseenter={onEnter}
	style:transform={`perspective(${perspective}px) rotateX(${state.rx}deg) rotateY(${state.ry}deg)`}
	style:transition={`transform ${state.hover ? 0.1 : 0.4}s cubic-bezier(0.23, 1, 0.32, 1), box-shadow ${state.hover ? 0.1 : 0.4}s cubic-bezier(0.23, 1, 0.32, 1)`}
	style:box-shadow={shadow ? `${s.x}px ${s.y}px ${s.blur}px rgba(0,0,0,${s.opacity})` : 'none'}
	class="h3d"
	data-depth={depth}
>
	{#if glare}
		<div
			class="glare"
			style:opacity={g.opacity}
			style:background={`radial-gradient(circle at ${g.x}% ${g.y}%, rgba(255,255,255,0.8), transparent)`}
		></div>
	{/if}
	<slot />
</div>

<style>
	.h3d {
		transform-style: preserve-3d;
		will-change: transform, box-shadow;
		position: relative;
	}

	.glare {
		position: absolute;
		inset: 0;
		border-radius: inherit;
		pointer-events: none;
		z-index: 1;
	}

	:global(.h3d > *:not(.glare)) {
		transform-style: preserve-3d;
		transition: inherit;
	}

	:global(.h3d[data-depth='true'] > *:not(.glare)) {
		transform: translateZ(20px);
	}
</style>
